const LAND_CAPABILITY_CLASS_INFO = {
  I: {
    title: 'Clase I',
    summary: 'Suelos con muy pocas o nulas limitaciones para uso agrícola intensivo.',
    bullets: [
      'Muy alta aptitud agrícola.',
      'Amplia adaptación para cultivos.',
      'Bajo riesgo de degradación bajo manejo adecuado.',
    ],
    management: [
      'Mantener rotaciones y cobertura.',
      'Sostener fertilidad química y biológica.',
      'Evitar compactación por tránsito innecesario.',
    ],
  },
  II: {
    title: 'Clase II',
    summary: 'Suelos con limitaciones moderadas que requieren prácticas de manejo y conservación.',
    bullets: [
      'Buena aptitud agrícola.',
      'Presentan restricciones moderadas.',
      'Requieren manejo más cuidadoso que clase I.',
    ],
    management: [
      'Implementar rotaciones.',
      'Sostener cobertura y materia orgánica.',
      'Aplicar prácticas de conservación según la limitación dominante.',
    ],
  },
  III: {
    title: 'Clase III',
    summary:
      'Suelos con limitaciones importantes que restringen la elección de cultivos y exigen manejo intensivo.',
    bullets: [
      'Aptitud agrícola moderada.',
      'Mayor riesgo de degradación.',
      'Menor flexibilidad de uso.',
    ],
    management: [
      'Priorizar conservación de suelo.',
      'Rotaciones con gramíneas y coberturas.',
      'Evitar usos extractivos sin reposición.',
    ],
  },
  IV: {
    title: 'Clase IV',
    summary: 'Suelos con limitaciones severas; la agricultura es restringida y de mayor riesgo.',
    bullets: [
      'Uso agrícola muy condicionado.',
      'Mayor susceptibilidad a degradación.',
      'Conviene evaluar sistemas mixtos o más conservacionistas.',
    ],
    management: [
      'Uso agrícola selectivo y de bajo impacto.',
      'Mayor participación de pasturas o coberturas.',
      'Conservación estricta del suelo.',
    ],
  },
  V: {
    title: 'Clase V',
    summary:
      'Suelos no aptos para agricultura de cultivo regular, pero con posible uso pastoril o similar.',
    bullets: [
      'Limitaciones permanentes importantes.',
      'No recomendados para agricultura continua.',
    ],
    management: [
      'Priorizar pasturas permanentes.',
      'Uso ganadero con manejo cuidadoso.',
    ],
  },
  VI: {
    title: 'Clase VI',
    summary:
      'Suelos no aptos para agricultura; su uso principal es pastoril, forestal o de conservación.',
    bullets: [
      'Severas limitaciones de uso.',
      'Muy alto riesgo bajo agricultura.',
    ],
    management: [
      'Uso pastoril extensivo o forestal.',
      'Conservación de cobertura.',
    ],
  },
  VII: {
    title: 'Clase VII',
    summary:
      'Suelos con limitaciones muy severas, principalmente destinados a conservación o uso muy restringido.',
    bullets: [
      'Muy baja aptitud agrícola.',
      'Uso fuertemente condicionado.',
    ],
    management: [
      'Conservación natural.',
      'Uso forestal o ganadero muy restringido según ambiente.',
    ],
  },
};

const LAND_SUBCLASS_INFO = {
  w: {
    code: 'w',
    title: 'Exceso de agua',
    shortTitle: 'exceso de agua',
    summary:
      'La principal limitación está asociada a drenaje deficiente, anegamiento, napa cercana o saturación hídrica.',
    bullets: [
      'Puede haber restricciones de aireación radicular.',
      'Mayor riesgo de compactación estructural.',
      'Puede limitar labores, implantación y desarrollo.',
    ],
    management: [
      'Evitar tránsito con suelo húmedo.',
      'Mejorar drenaje superficial y/o subsuperficial si el ambiente lo permite.',
      'Elegir rotaciones y especies tolerantes a excesos hídricos.',
    ],
  },
  s: {
    code: 's',
    title: 'Limitaciones edáficas',
    shortTitle: 'limitaciones edáficas',
    summary:
      'La principal limitación está dada por propiedades del suelo: textura, profundidad, pedregosidad, salinidad, sodicidad, fertilidad u otras condiciones físicas/químicas.',
    bullets: [
      'Puede restringir exploración radicular.',
      'Puede limitar disponibilidad de agua o nutrientes.',
      'Puede exigir manejo agronómico específico.',
    ],
    management: [
      'Aumentar materia orgánica y estabilidad estructural.',
      'Ajustar nutrición y enmiendas según diagnóstico.',
      'Evitar prácticas que agraven compactación o degradación.',
    ],
  },
  e: {
    code: 'e',
    title: 'Riesgo de erosión',
    shortTitle: 'riesgo de erosión',
    summary:
      'La principal limitación está asociada al riesgo o presencia de erosión hídrica o eólica.',
    bullets: [
      'Pérdida potencial o efectiva de horizonte superficial.',
      'Reducción de productividad si no se conserva.',
      'Mayor sensibilidad al manejo del relieve y cobertura.',
    ],
    management: [
      'Mantener cobertura permanente.',
      'Aplicar siembra directa, curvas de nivel, terrazas o prácticas equivalentes.',
      'Reducir períodos de suelo desnudo.',
    ],
  },
  c: {
    code: 'c',
    title: 'Limitaciones climáticas',
    shortTitle: 'limitaciones climáticas',
    summary:
      'La principal restricción está asociada al clima: temperatura, déficit hídrico, heladas u otras condiciones ambientales regionales.',
    bullets: [
      'Condiciona elección de cultivos y fechas de siembra.',
      'Puede limitar estabilidad de rendimiento.',
    ],
    management: [
      'Seleccionar cultivos y genotipos adaptados.',
      'Ajustar calendario de implantación.',
      'Manejo del agua según disponibilidad.',
    ],
  },
};

const SUBCLASS_CANONICAL_ORDER = ['w', 's', 'e', 'c'];
const CLASS_ORDER = ['VII', 'VI', 'V', 'IV', 'III', 'II', 'I'];

export function interpretProductivityIndex(ipRaw) {
  const ip = Number(ipRaw);

  if (!Number.isFinite(ip)) {
    return {
      value: null,
      level: 'Sin dato',
      summary: 'No se pudo interpretar el índice de productividad.',
      bullets: [],
    };
  }

  if (ip >= 80) {
    return {
      value: ip,
      level: 'Muy alto',
      summary: 'Muy buena productividad potencial.',
      bullets: [
        'Ambiente con alto potencial relativo.',
        'Bajo nivel de restricción general respecto de otras unidades.',
      ],
    };
  }

  if (ip >= 60) {
    return {
      value: ip,
      level: 'Alto',
      summary: 'Buena productividad potencial.',
      bullets: [
        'Buen comportamiento productivo relativo.',
        'Las limitaciones presentes permiten aún niveles productivos favorables.',
      ],
    };
  }

  if (ip >= 40) {
    return {
      value: ip,
      level: 'Medio',
      summary: 'Productividad potencial intermedia.',
      bullets: [
        'La respuesta productiva depende más del ambiente y del manejo.',
        'Las limitaciones comienzan a impactar con mayor peso.',
      ],
    };
  }

  if (ip >= 20) {
    return {
      value: ip,
      level: 'Bajo',
      summary: 'Productividad potencial baja.',
      bullets: [
        'Existen restricciones relevantes.',
        'El manejo conservacionista es clave para sostener producción.',
      ],
    };
  }

  return {
    value: ip,
    level: 'Muy bajo',
    summary: 'Productividad potencial muy baja.',
    bullets: [
      'Limitaciones severas del ambiente.',
      'El uso debe priorizar conservación y baja intensidad.',
    ],
  };
}

export function normalizeCapabilityClass(value) {
  if (value === null || value === undefined) return '';
  return String(value).trim().toUpperCase();
}

export function normalizeSubclass(value) {
  if (value === null || value === undefined) return '';

  const chars = String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z]/g, '')
    .split('');

  const unique = [...new Set(chars)].filter((c) => SUBCLASS_CANONICAL_ORDER.includes(c));

  unique.sort(
    (a, b) => SUBCLASS_CANONICAL_ORDER.indexOf(a) - SUBCLASS_CANONICAL_ORDER.indexOf(b)
  );

  return unique.join('');
}

export function buildCapabilityKey(clase, subclase) {
  const c = normalizeCapabilityClass(clase);
  const s = normalizeSubclass(subclase);
  return `${c}${s}`;
}

export function getSubclassCodes(subclase) {
  return normalizeSubclass(subclase).split('').filter(Boolean);
}

export function parseCapabilityFromCap(capValue) {
  if (capValue === null || capValue === undefined) {
    return {
      raw: '',
      clase: '',
      subclase: '',
      capabilityKey: '',
    };
  }

  const raw = String(capValue).trim();

  if (!raw) {
    return {
      raw: '',
      clase: '',
      subclase: '',
      capabilityKey: '',
    };
  }

  const upperRaw = raw.toUpperCase();

  const clase = CLASS_ORDER.find((roman) => upperRaw.startsWith(roman)) ?? '';
  const subclaseRaw = clase ? raw.slice(clase.length) : '';
  const subclase = normalizeSubclass(subclaseRaw);
  const capabilityKey = buildCapabilityKey(clase, subclase);

  return {
    raw,
    clase,
    subclase,
    capabilityKey,
  };
}

export function sortCartaSueloEntries(entries = []) {
  const priority = ['simbolo', 'cap', 'clase', 'subclase', 'ip'];

  return [...entries].sort(([a], [b]) => {
    const ia = priority.indexOf(String(a).toLowerCase());
    const ib = priority.indexOf(String(b).toLowerCase());

    const pa = ia === -1 ? 999 : ia;
    const pb = ib === -1 ? 999 : ib;

    return pa - pb;
  });
}

function formatListWithAnd(items = []) {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} y ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} y ${items[items.length - 1]}`;
}

export function getCapabilityInterpretation(props = {}) {
  const parsedFromCap = parseCapabilityFromCap(props.cap ?? props.cap_uso);

  const clase = normalizeCapabilityClass(props.clase || parsedFromCap.clase);
  const subclase = normalizeSubclass(props.subclase || parsedFromCap.subclase);

  const capabilityKey = buildCapabilityKey(clase, subclase);

  const rawCapUso = props.cap
    ? String(props.cap).trim()
    : props.cap_uso
      ? String(props.cap_uso).trim()
      : '';

  const capUso = rawCapUso || capabilityKey;

  const classInfo = LAND_CAPABILITY_CLASS_INFO[clase] ?? null;

  const subclassCodes = getSubclassCodes(subclase);
  const subclassInfoList = subclassCodes
    .map((code) => LAND_SUBCLASS_INFO[code])
    .filter(Boolean);

  const ipInfo = interpretProductivityIndex(props.ip);

  const subclassShortTitles = subclassInfoList.map((x) => x.shortTitle);
  const subclassTitles = subclassInfoList.map((x) => x.title.toLowerCase());

  const summaryParts = [];
  if (classInfo?.summary) summaryParts.push(classInfo.summary);

  if (subclassShortTitles.length > 0) {
    summaryParts.push(
      `La unidad presenta limitaciones dominantes asociadas a ${formatListWithAnd(subclassShortTitles)}.`
    );
  }

  const bullets = [
    ...(classInfo?.bullets ?? []),
    ...subclassInfoList.flatMap((x) => [x.summary, ...(x.bullets ?? [])]),
  ];

  const management = [
    ...(classInfo?.management ?? []),
    ...subclassInfoList.flatMap((x) => x.management ?? []),
  ];

  return {
    capabilityKey,
    capUso,
    rawCapUso,
    clase,
    subclase,
    classInfo,
    subclassInfoList,
    ipInfo,
    title: capUso ? `Capacidad de Uso ${capUso}` : 'Capacidad de Uso',
    summary:
      summaryParts.join(' ') ||
      (subclassTitles.length > 0
        ? `Unidad con limitaciones asociadas a ${formatListWithAnd(subclassTitles)}.`
        : 'No hay interpretación configurada para esta capacidad de uso.'),
    bullets: [...new Set(bullets)],
    management: [...new Set(management)],
  };
}